import get from 'lodash.get';
import { useState, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { ImageMultiSkuOverlay } from 'dibs-image-multi-sku-overlay/exports/ImageMultiSkuOverlay';
import { RingSizeDisplay } from '../shared/components/RingSizeDisplay';
import { trackUserInteractionOnce } from '../helpers/checkoutTracking';
import { useCheckoutSelector } from '../reducers/root';
import styles from './styles/CheckoutSidebarItemInfo.scss';

const CheckoutSidebarItemInfo = props => {
    const {
        item: itemRef,
        buyer: buyerRef,
        transaction: transactionRef,
        wrapperClass = '',
    } = props;

    const item = useFragment(
        graphql`
            fragment CheckoutSidebarItemInfo_item on Item {
                ...CheckoutZoomImageModal_item
                ...ImageMultiSkuOverlay_item
                ...RingSizeDisplay_item
                title
                vertical
                isArt
                isMultiSku
                isRingItem
                creators {
                    creator {
                        name
                    }
                }
                seller {
                    sellerProfile {
                        company
                    }
                }
                skus {
                    serviceId
                    ...ImageMultiSkuOverlay_sku
                }
            }
        `,
        itemRef
    );
    const skuId = useCheckoutSelector(state => state.transaction.skuId);
    const sku = item?.skus?.find(itemSku => itemSku?.serviceId === skuId);

    const buyer = useFragment(
        graphql`
            fragment CheckoutSidebarItemInfo_buyer on User {
                canShowSellerName
            }
        `,
        buyerRef
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutSidebarItemInfo_transaction on Transaction {
                ...RingSizeDisplay_transaction
                skuVariableAttributeRows {
                    label
                    value
                }
                sku {
                    leadTime(pageType: Checkout)
                }
            }
        `,
        transactionRef
    );

    const [isModalOpen, setZoomModal] = useState(false);
    const { title, isArt, isMultiSku, isRingItem, creators } = item;
    const { canShowSellerName } = buyer || {};
    const variableAttributeRows = transaction?.skuVariableAttributeRows || [];
    const leadTime = get(transaction, 'sku.leadTime');
    const isMskuRing = isMultiSku && isRingItem;
    const showLeadTime = (!isMultiSku || isMskuRing) && !!leadTime;

    const leadTimeComponent = showLeadTime ? (
        <div className={styles.itemLeadTime}>
            <FormattedMessage
                id="confirmation.CheckoutSidebarItemInfo.leadTime"
                defaultMessage="<wrapper>Production Time: </wrapper>{leadTime}"
                values={{
                    leadTime: <span data-tn="checkout-item-lead-time">{leadTime}</span>,
                    wrapper: content => (
                        <span className={styles.itemLeadTimeMessage}>{content}</span>
                    ),
                }}
            />
        </div>
    ) : null;

    const CheckoutZoomImageModal = lazy(() =>
        import(
            /* webpackChunkName: "checkout-zoom-modal" */
            './CheckoutZoomImageModal'
        )
    );

    function openZoomModal() {
        setZoomModal(true);
        trackUserInteractionOnce();
    }

    return (
        <div
            className={classNames(wrapperClass, styles.wrapper)}
            onClick={() => openZoomModal()}
            onKeyDown={() => openZoomModal()}
            role="button"
            tabIndex={0}
        >
            <div>
                <div className={styles.imageWrapper} data-tn="checkout-item-review-image">
                    <ImageMultiSkuOverlay imageAlt={title} item={item} sku={sku} useSuspense />
                    {isModalOpen && !CheckoutZoomImageModal && (
                        <div className={styles.spinnerOverlay}>
                            <Spinner dark />
                        </div>
                    )}
                </div>
            </div>
            <div>
                {isArt && (
                    <span className={styles.itemArtist} data-tn="checkout-item-review-creator">
                        {get(creators, '[0].creator.name')}
                    </span>
                )}

                <span data-tn="checkout-item-review-title" className={styles.itemTitle}>
                    {title}
                </span>

                {isMultiSku && !isRingItem && (
                    <div className={styles.attributesAndLeadTime}>
                        {variableAttributeRows.map((row, index) => (
                            <div key={index} className={styles.itemVariableAttribute}>
                                <span
                                    data-tn="checkout-item-review-attribute-name"
                                    className={styles.itemVariableAttributeName}
                                >
                                    {row?.label}
                                </span>
                                {': '}
                                <span
                                    data-tn="checkout-item-review-attribute-option"
                                    className={styles.itemVariableAttributeOption}
                                >
                                    {row?.value}
                                </span>
                            </div>
                        ))}
                        {leadTimeComponent}
                    </div>
                )}

                {isRingItem && (
                    <div className={styles.ringSizeDisplay}>
                        <RingSizeDisplay
                            item={item}
                            transaction={transaction}
                            paddingTop={'none'}
                        />
                    </div>
                )}

                {leadTimeComponent}

                {canShowSellerName && (
                    <div className={styles.itemOfferedBy}>
                        <span>
                            <FormattedMessage
                                id="checkout.CheckoutSidebarItemInfo.offeredBy"
                                defaultMessage="Offered By {seller}"
                                values={{
                                    seller: (
                                        <span
                                            data-tn="checkout-item-review-seller"
                                            className={styles.itemSeller}
                                        >
                                            {get(item, 'seller.sellerProfile.company')}
                                        </span>
                                    ),
                                }}
                            />
                        </span>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <Suspense fallback={''}>
                    <CheckoutZoomImageModal
                        item={item}
                        transaction={transaction}
                        closeModal={() => setZoomModal(false)}
                    />
                </Suspense>
            )}
        </div>
    );
};

CheckoutSidebarItemInfo.propTypes = {
    item: PropTypes.any.isRequired,
    buyer: PropTypes.object,
    transaction: PropTypes.object,
    wrapperClass: PropTypes.string,
};

export default CheckoutSidebarItemInfo;
